import { useState, useEffect, useCallback } from 'react';
import { UseCaseDetail, UseCaseStatus } from '../types'; // Ajustez le chemin si nécessaire
import useCaseService from '../services/useCaseService'; // Importer le client API
import i18n from '../i18n/config';

// Type pour les données mises à jour (partiel car on ne modifie pas tout)
type UseCaseUpdatePayload = Partial<Omit<UseCaseDetail, 'id' | 'auditId' | 'cadrageId' | 'personas' | 'evaluation' | 'analysis'>>;

interface UseUseCaseEditorResult {
  isEditing: boolean;
  isSaving: boolean; // Nouvel état pour le chargement de la sauvegarde
  saveError: string | null; // Nouvel état pour l'erreur de sauvegarde
  editedName: string;
  editedStatus: string;
  editedShortDescription: string;
  editedDescription: string; // Ajouter description
  editedRecommendation: string;
  edited_value_score: number; // Correction: camelCase -> snake_case
  edited_complexity_score: number; // Correction: camelCase -> snake_case
  editedBusinessNeed: string;
  editedInput: string;
  editedOutput: string;
  editedInvolvedTools: string[];
  editedTechElements: string;
  customToolInput: string;
  setEditedName: React.Dispatch<React.SetStateAction<string>>;
  setEditedStatus: React.Dispatch<React.SetStateAction<string>>;
  setEditedShortDescription: React.Dispatch<React.SetStateAction<string>>;
  setEditedDescription: React.Dispatch<React.SetStateAction<string>>;
  setEditedRecommendation: React.Dispatch<React.SetStateAction<string>>;
  set_edited_value_score: React.Dispatch<React.SetStateAction<number>>; // Correction: camelCase -> snake_case
  set_edited_complexity_score: React.Dispatch<React.SetStateAction<number>>; // Correction: camelCase -> snake_case
  setEditedBusinessNeed: React.Dispatch<React.SetStateAction<string>>;
  setEditedInput: React.Dispatch<React.SetStateAction<string>>;
  setEditedOutput: React.Dispatch<React.SetStateAction<string>>;
  setEditedInvolvedTools: React.Dispatch<React.SetStateAction<string[]>>;
  setEditedTechElements: React.Dispatch<React.SetStateAction<string>>;
  setCustomToolInput: React.Dispatch<React.SetStateAction<string>>;
  handleEditToggle: () => void;
  handleSaveChanges: () => Promise<boolean>;
  resetEditedFields: () => void;
}

// Le hook prend seulement les données initiales
export const useUseCaseEditor = (
  initialUseCase: UseCaseDetail | null | undefined // Accepter undefined
  // saveCallback est supprimé
): UseUseCaseEditorResult => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // État pour le chargement de la sauvegarde
  const [saveError, setSaveError] = useState<string | null>(null); // État pour l'erreur de sauvegarde

  // États temporaires pour les champs édités (inchangés)
  const [editedName, setEditedName] = useState('');
  const [editedStatus, setEditedStatus] = useState('');
  const [editedShortDescription, setEditedShortDescription] = useState('');
  const [editedDescription, setEditedDescription] = useState(''); // Ajouter état
  const [editedRecommendation, setEditedRecommendation] = useState('');
  const [edited_value_score, set_edited_value_score] = useState<number>(5); // Correction: camelCase -> snake_case
  const [edited_complexity_score, set_edited_complexity_score] = useState<number>(5); // Correction: camelCase -> snake_case
  const [editedBusinessNeed, setEditedBusinessNeed] = useState('');
  const [editedInput, setEditedInput] = useState('');
  const [editedOutput, setEditedOutput] = useState('');
  const [editedInvolvedTools, setEditedInvolvedTools] = useState<string[]>([]);
  const [customToolInput, setCustomToolInput] = useState('');
  const [editedTechElements, setEditedTechElements] = useState('');

  // Fonction pour réinitialiser les champs édités (inchangée)
  const resetEditedFields = useCallback(() => {
    if (initialUseCase) {
      setEditedName(initialUseCase.name);
      setEditedStatus(initialUseCase.status);
      setEditedShortDescription(initialUseCase.short_description || '');
      setEditedDescription(initialUseCase.description || '');
      setEditedRecommendation(initialUseCase.recommendation || '');
      // Initialiser les scores, avec une valeur par défaut si null/undefined
      set_edited_value_score(initialUseCase.value_score ?? 5); // Correction: camelCase -> snake_case
      set_edited_complexity_score(initialUseCase.complexity_score ?? 5); // Correction: camelCase -> snake_case
      setEditedBusinessNeed(initialUseCase.business_need || '');
      setEditedInput(initialUseCase.input || '');
      setEditedOutput(initialUseCase.output || '');
      setEditedInvolvedTools(initialUseCase.involved_tools || []);
      setEditedTechElements(initialUseCase.technical_elements || '');
      setCustomToolInput('');
    }
  }, [initialUseCase]);

  // Initialiser ou réinitialiser les champs (inchangé)
  useEffect(() => {
    resetEditedFields();
  }, [initialUseCase, resetEditedFields]);

  // Gérer le basculement du mode édition (modifié pour reset l'erreur)
  const handleEditToggle = useCallback(() => {
    if (isEditing) {
      resetEditedFields();
      setSaveError(null); // Réinitialiser l'erreur de sauvegarde en annulant
    }
    setIsEditing((prev) => !prev);
  }, [isEditing, resetEditedFields]);

  // Gérer la sauvegarde des changements (modifié)
  const handleSaveChanges = useCallback(async (): Promise<boolean> => { // Modifier la signature pour retourner Promise<boolean>
    if (!initialUseCase || !initialUseCase.id) {
        const errorMsg = i18n.t('errors.useCase.missingId');
        console.error("Impossible de sauvegarder : ID du cas d'usage manquant.");
        setSaveError(errorMsg);
        // Lever une erreur pour indiquer l'échec à l'appelant
        throw new Error(errorMsg);
    }

    setIsSaving(true);
    setSaveError(null);

    const payload: UseCaseUpdatePayload = {
      name: editedName,
      status: editedStatus as UseCaseStatus,
      short_description: editedShortDescription,
      description: editedDescription,
      recommendation: editedRecommendation,
      value_score: edited_value_score, // Correction: camelCase -> snake_case
      complexity_score: edited_complexity_score, // Correction: camelCase -> snake_case
      business_need: editedBusinessNeed,
      input: editedInput,
      output: editedOutput,
      involved_tools: editedInvolvedTools,
      technical_elements: editedTechElements,
    };

    try {
      console.log(`useUseCaseEditor: Saving data for ID: ${initialUseCase.id} via API...`, payload);
      await useCaseService.update(initialUseCase.id, payload);
      console.log(`useUseCaseEditor: Successfully saved data for ID: ${initialUseCase.id}`);
      setIsEditing(false); // Quitter le mode édition après succès
      setIsSaving(false); // Mettre à jour isSaving ici en cas de succès
      return true; // Retourner true en cas de succès
    } catch (error: any) {
      console.error(`useUseCaseEditor: Error saving use case ${initialUseCase.id}:`, error);
      const errorMsg = error.response?.data?.detail || error.message || i18n.t('errors.useCase.saveFailed');
      setSaveError(errorMsg);
      setIsSaving(false); // Mettre à jour isSaving ici en cas d'erreur
      // Lever une erreur pour indiquer l'échec à l'appelant
      throw new Error(errorMsg);
    }
    // Le bloc finally n'est plus nécessaire car isSaving est géré dans try/catch
  }, [
    initialUseCase,
    editedName,
    editedStatus,
    editedShortDescription,
    editedDescription,
    editedRecommendation,
    edited_value_score, // Correction: camelCase -> snake_case
    edited_complexity_score, // Correction: camelCase -> snake_case
    editedBusinessNeed,
    editedInput,
    editedOutput,
    editedInvolvedTools,
    editedTechElements,
  ]);

  return {
    isEditing,
    isSaving, // Retourner le nouvel état
    saveError, // Retourner le nouvel état
    editedName,
    editedStatus,
    editedShortDescription,
    editedDescription,
    editedRecommendation,
    edited_value_score, // Correction: camelCase -> snake_case
    edited_complexity_score, // Correction: camelCase -> snake_case
    editedBusinessNeed,
    editedInput,
    editedOutput,
    editedInvolvedTools,
    editedTechElements,
    customToolInput,
    setEditedName,
    setEditedStatus,
    setEditedShortDescription,
    setEditedDescription,
    setEditedRecommendation,
    set_edited_value_score, // Correction: camelCase -> snake_case
    set_edited_complexity_score, // Correction: camelCase -> snake_case
    setEditedBusinessNeed,
    setEditedInput,
    setEditedOutput,
    setEditedInvolvedTools,
    setEditedTechElements,
    setCustomToolInput,
    handleEditToggle,
    handleSaveChanges,
    resetEditedFields,
  };
};
